import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { AlertTriangle, TrendingUp, Layers, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PrazosInsights · Central de Prazos (Hermes)
 * - Radar donut · distribuição de prazos abertos por bucket (vencido/hoje/semana/30d/longe)
 * - DJEN por dia · publicações dos últimos 14 dias
 * - Por fase · prazos abertos agrupados por expedient_type
 */

const COLORS = ["#0f172a", "#dc2626", "#f59e0b", "#eab308", "#10b981"]; // 5 buckets
const NAVY = "#0B1B2F";

// Mapa bucket → filter aceito por /prazos
const BUCKET_TO_FILTER = {
  vencido:    "vencido",
  hoje:       "24h",
  semana:     "7d",
  proximos30: "future",
  longe:      "future",
};

export default function PrazosInsights({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const onSliceClick = (payload) => {
    // Recharts 3.x envia o data-item como primeiro arg do onClick
    const item = payload?.payload || payload;
    const bucket = item?.key;
    const filterVal = BUCKET_TO_FILTER[bucket];
    if (filterVal) navigate(`/prazos?filter=${filterVal}`);
    else navigate("/prazos");
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/deadlines/insights");
        setData(r.data);
      } catch (e) {
        // silent
      } finally { setLoading(false); }
    })();
  }, []);

  const radar = data?.radar_donut || [];
  const totalOpen = data?.total_open || 0;
  const djen = data?.djen_by_day || [];
  const byStage = data?.by_stage || [];

  const djenTotal = useMemo(() => djen.reduce((s, d) => s + (d.count || 0), 0), [djen]);
  const stageTotal = useMemo(() => byStage.reduce((s, d) => s + (d.count || 0), 0), [byStage]);

  const noData = !loading && totalOpen === 0 && djenTotal === 0 && stageTotal === 0;

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 text-center text-sm text-slate-500"
           data-testid="prazos-insights-loading">
        Carregando insights de prazos…
      </div>
    );
  }
  if (noData) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/70 shadow-sm p-6 text-center text-sm text-slate-500"
           data-testid="prazos-insights-empty">
        Sem prazos abertos ainda — quando o DJEN começar a sincronizar, os gráficos aparecerão aqui.
      </div>
    );
  }

  // -------- COMPACT (Dashboard) · só o radar donut ---------
  if (compact) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="prazos-radar-compact">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600"/>
            <div className="text-sm font-semibold" style={{ color: NAVY }}>Radar de prazos</div>
          </div>
          <a href="/prazos" className="text-[10.5px] text-slate-500 hover:text-slate-800">Ver todos →</a>
        </div>
        <div className="text-[11px] text-slate-500 mb-2">
          {totalOpen} prazo{totalOpen !== 1 ? "s" : ""} aberto{totalOpen !== 1 ? "s" : ""} · distribuição por urgência
        </div>
        <div style={{ height: 210 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={radar} dataKey="count" nameKey="label"
                   innerRadius={48} outerRadius={72} paddingAngle={2}
                   stroke="#fff" strokeWidth={2}
                   cursor="pointer">
                {radar.map((entry, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]}
                        onClick={() => onSliceClick(entry)}
                        style={{ cursor: "pointer" }}/>
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} prazo${v !== 1 ? "s" : ""} · clique para filtrar`, n]}
                       contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}/>
              <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 10, paddingTop: 4 }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
         data-testid="prazos-insights">
      <button
        onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 hover:bg-slate-50"
        data-testid="prazos-insights-toggle">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-900 text-white">
            <BarChart3 className="w-4 h-4"/>
          </div>
          <div className="text-left">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Central de Prazos</div>
            <div className="font-heading text-lg font-semibold" style={{ color: NAVY }}>
              Panorama operacional
            </div>
          </div>
        </div>
        <span className="text-xs text-slate-500">{collapsed ? "Expandir" : "Ocultar"}</span>
      </button>

      {!collapsed && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
          {/* 1) Radar donut */}
          <div className="p-5" data-testid="insights-radar">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-rose-600"/>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>Radar de prazos</div>
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              {totalOpen} prazo{totalOpen !== 1 ? "s" : ""} aberto{totalOpen !== 1 ? "s" : ""} · distribuição por urgência
            </div>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={radar}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={52}
                    outerRadius={80}
                    paddingAngle={2}
                    stroke="#fff"
                    strokeWidth={2}
                    cursor="pointer"
                  >
                    {radar.map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]}
                            onClick={() => onSliceClick(entry)}
                            style={{ cursor: "pointer" }}/>
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [`${v} prazo${v !== 1 ? "s" : ""} · clique para filtrar`, n]}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 2) DJEN por dia */}
          <div className="p-5" data-testid="insights-djen">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-sky-600"/>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>Publicações DJEN · 14 dias</div>
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              {djenTotal} publicaç{djenTotal !== 1 ? "ões" : "ão"} recebida{djenTotal !== 1 ? "s" : ""} no período
            </div>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={djen} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9, fill: "#64748b" }}
                    tickFormatter={(d) => d.slice(5).replace("-", "/")}
                    interval={1}
                  />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} width={26}/>
                  <Tooltip
                    formatter={(v) => [`${v} publicaç${v !== 1 ? "ões" : "ão"}`, "Total"]}
                    labelFormatter={(l) => new Date(l + "T00:00:00").toLocaleDateString("pt-BR")}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  />
                  <Bar dataKey="count" fill="#0284c7" radius={[3, 3, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 3) Por fase */}
          <div className="p-5" data-testid="insights-stage">
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4 text-emerald-600"/>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>Prazos por tipo</div>
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              {stageTotal} prazo{stageTotal !== 1 ? "s" : ""} abertos por tipo de expediente
            </div>
            {byStage.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-10 text-center">Sem prazos abertos.</div>
            ) : (
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byStage} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false}/>
                    <YAxis
                      type="category"
                      dataKey="stage"
                      tick={{ fontSize: 10, fill: "#334155" }}
                      width={110}
                      interval={0}
                    />
                    <Tooltip
                      formatter={(v) => [`${v} prazo${v !== 1 ? "s" : ""}`, "Aberto(s)"]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                    />
                    <Bar dataKey="count" fill="#059669" radius={[0, 3, 3, 0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
